<template>
  <BasePopup
    :show="show"
    title="Codice di accesso"
    close-test-id="login-code-close"
    :close-on-backdrop="false"
    card-class="w-full md:w-[480px] lg:p-8 lg:pt-6 p-6 pt-4 flex flex-col nap-login-screen"
    @close="close"
  >
    <form class="space-y-4" data-testid="login-code-form" @submit.prevent="verify">
      <p class="text-sm text-black leading-5">
        Per completare l'accesso abbiamo inviato un codice di 6 cifre a
        <strong class="text-black break-all">{{ maskedEmail }}</strong>. Inseriscilo qui sotto.
      </p>

      <OtpCodeBoxes
        ref="boxes"
        :code="shownCode"
        :length="codeLength"
        :state="boxState"
        data-testid="login-code-input"
        aria-label="Codice di accesso"
        :value="code"
        :disabled="verifying || finished || boxState === 'success'"
        @input="onInput"
        @paste.prevent="onPaste"
      />

      <p v-if="verifying" data-testid="login-code-verifying" class="text-center text-gray-500 text-sm">
        Verifica in corso...
      </p>
      <p v-else-if="boxState === 'success'" data-testid="login-code-success" class="text-center text-primary text-sm font-semibold">
        Accesso confermato
      </p>
      <p v-else-if="errorMessage" data-testid="login-code-error" class="text-center text-red-500 text-sm">
        {{ errorMessage }}
      </p>
      <p v-else-if="infoMessage" data-testid="login-code-info" class="text-center text-gray-500 text-sm">
        {{ infoMessage }}
      </p>

      <div class="flex items-center justify-between text-sm text-gray-500">
        <button type="button" data-testid="login-code-cancel" class="underline hover:text-primary" @click="close">
          Annulla
        </button>
        <button
          type="button"
          data-testid="login-code-resend"
          class="underline hover:text-primary disabled:no-underline disabled:opacity-60"
          :disabled="sending || finished || secondsLeft > 0 || boxState === 'success'"
          @click="resend"
        >
          {{ secondsLeft > 0 ? `Invia di nuovo tra ${secondsLeft} s` : "Invia di nuovo" }}
        </button>
      </div>
    </form>
  </BasePopup>
</template>

<script>
// Email code step of the website password login (#666). The parent has
// already checked the password and sent the code (services/loginCode.js);
// this popup verifies it through `flow` and emits `verified` once the site
// session is signed in. Annulla and × emit `close`. There is no submit
// button: the sixth digit verifies, and a right code plays the boxes'
// success animation before `verified`.
import BasePopup from "@/components/BasePopup.vue";
import OtpCodeBoxes, { OTP_SUCCESS_MS } from "@/components/OtpCodeBoxes.vue";
import { LOGIN_CODE_LENGTH } from "@/services/loginCode.js";

export default {
  name: "LoginCodePopup",
  components: { BasePopup, OtpCodeBoxes },
  props: {
    show: { type: Boolean, default: false },
    maskedEmail: { type: String, default: "" },
    resendInSec: { type: Number, default: 60 },
    // { verify(code), resend() } from createLoginCodeFlow.
    flow: { type: Object, default: null },
    // How long the success animation plays before `verified` (0 in tests).
    successMs: { type: Number, default: OTP_SUCCESS_MS },
  },
  emits: ["verified", "close"],
  data() {
    return {
      code: "",
      codeLength: LOGIN_CODE_LENGTH,
      verifying: false,
      sending: false,
      finished: false,
      secondsLeft: 0,
      errorMessage: "",
      infoMessage: "",
      timer: null,
      boxState: "",
      failedCode: "",
      errorTimer: null,
    };
  },
  computed: {
    // A wrong code leaves the field at once; the boxes keep it while they shake.
    shownCode() {
      return this.boxState === "error" ? this.failedCode : this.code;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(open) {
        if (open) this.reset();
        else this.stopTimer();
      },
    },
    // A new login attempt handed in while the popup is open starts clean too:
    // nothing from the previous attempt (error, lock, cooldown) carries over.
    flow(next, previous) {
      if (this.show && next !== previous) this.reset();
    },
  },
  beforeUnmount() {
    this.stopTimer();
    clearTimeout(this.errorTimer);
  },
  methods: {
    reset() {
      this.code = "";
      this.failedCode = "";
      this.verifying = false;
      this.sending = false;
      this.finished = false;
      this.setBoxState("");
      this.errorMessage = "";
      this.infoMessage = "Codice inviato. Controlla la tua casella email.";
      this.startTimer(this.resendInSec);
      this.$nextTick(() => this.$refs.boxes && this.$refs.boxes.focus());
    },
    startTimer(seconds) {
      this.stopTimer();
      this.secondsLeft = Math.max(0, Math.ceil(Number(seconds) || 0));
      if (this.secondsLeft === 0) return;
      this.timer = setInterval(() => {
        this.secondsLeft = Math.max(0, this.secondsLeft - 1);
        if (this.secondsLeft === 0) this.stopTimer();
      }, 1000);
    },
    setBoxState(state) {
      clearTimeout(this.errorTimer);
      this.boxState = state;
      if (state === "error") this.errorTimer = setTimeout(() => (this.boxState = ""), 800);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // Copiato dall'email il codice porta spesso con sé a capo o tabulazioni (sta in
    // una cella di tabella): niente maxlength, che lo troncava prima di onInput,
    // e l'incolla passa da qui tenendo solo le cifre (#767).
    onPaste(event) {
      event.target.value = event.clipboardData ? event.clipboardData.getData("text") : "";
      this.onInput(event);
    },
    onInput(event) {
      this.code = String(event.target.value || "").replace(/\D+/g, "").slice(0, LOGIN_CODE_LENGTH);
      event.target.value = this.code;
      this.errorMessage = "";
      if (this.boxState === "error") this.setBoxState("");
      if (this.code.length === LOGIN_CODE_LENGTH) this.verify();
    },
    async verify() {
      if (this.verifying || this.finished || this.boxState === "success" || this.code.length !== LOGIN_CODE_LENGTH || !this.flow) return;
      this.verifying = true;
      this.setBoxState("verifying");
      this.errorMessage = "";
      this.infoMessage = "";
      const result = await this.flow.verify(this.code);
      this.verifying = false;
      if (result.ok) {
        this.stopTimer();
        this.setBoxState("success");
        if (this.successMs > 0) await new Promise((r) => setTimeout(r, this.successMs));
        this.$emit("verified");
        return;
      }
      this.errorMessage = result.message;
      this.failedCode = this.code;
      this.code = "";
      this.setBoxState("error");
      if (!result.final) this.$nextTick(() => this.$refs.boxes && this.$refs.boxes.focus());
      if (result.final) {
        this.finished = true;
        this.stopTimer();
      } else if (result.stale) {
        // No code can work on this attempt any more: "Invia di nuovo" starts
        // a new one, so it must not wait for the cooldown (#861). The server's
        // send limits still apply to it.
        this.startTimer(0);
      }
    },
    async resend() {
      if (this.sending || this.finished || this.secondsLeft > 0 || !this.flow) return;
      this.sending = true;
      this.errorMessage = "";
      this.infoMessage = "Invio del codice in corso...";
      const result = await this.flow.resend();
      this.sending = false;
      if (result.ok) {
        this.code = "";
        this.failedCode = "";
        this.setBoxState("");
        this.errorMessage = "";
        this.infoMessage = result.message || "Ti abbiamo inviato un nuovo codice. Controlla la tua casella email.";
        this.startTimer(result.resendInSec);
        this.$nextTick(() => this.$refs.boxes && this.$refs.boxes.focus());
        return;
      }
      this.infoMessage = "";
      this.errorMessage = result.message;
      if (result.final) {
        this.finished = true;
        this.stopTimer();
      } else {
        // Never an instant retry loop on a refusal: the server's wait when it
        // gives one, else the usual cooldown.
        this.startTimer(typeof result.resendInSec === "number" ? result.resendInSec : this.resendInSec);
      }
    },
    close() {
      if (this.verifying || this.boxState === "success") return;
      this.stopTimer();
      this.$emit("close");
    },
  },
};
</script>
